/**
 * Feed / Search (§7.3, below the fold).
 *
 * Two disclosures that stop this panel from lying:
 *  - cache age, because results cache for 15 minutes and a 14-minute-old feed
 *    presented as live is a wrong answer, not a slow one
 *  - rate-limit headroom BEFORE a search runs, so the actor spends the last of
 *    the quota on purpose
 *
 * Empty, adapter failure and rate limited are three different screens (§7.6);
 * rendering them identically is the specific failure the spec calls out.
 */

import { useState } from 'react';
import { EMPTY_STATES } from '@pm/shared';
import { useApi } from '../../hooks/useApi';
import type { Bot, FeedResult } from '../../api/types';
import type { CapabilityResolver } from '../../hooks/useCapabilities';
import { Gate } from '../../components/Gate';
import { Card, Field, Loading, Badge } from '../../components/ui';
import { EmptyState } from '../../components/EmptyState';
import { ErrorView } from '../../components/ErrorView';
import { interpolate, localDateTime, relativeAge } from '../../lib/format';
import { routes } from '../../api/routes';
import { preferredAccount } from '../../lib/accounts';

function CacheLine({ result }: { result: FeedResult }) {
  return (
    <p className="cache-line">
      {result.cached ? (
        <>
          <Badge tone="warn">cached</Badge> {relativeAge(result.cache_age_seconds)} · results cache
          for 15 minutes
        </>
      ) : (
        <>
          <Badge tone="ok">live</Badge> fetched just now
        </>
      )}
    </p>
  );
}

function Headroom({ result }: { result: FeedResult | null }) {
  const rl = result?.rate_limit;
  if (!rl || rl.remaining === null) {
    return <p className="muted">Rate-limit headroom is unknown until the first read.</p>;
  }
  const low = rl.limit !== null && rl.remaining / Math.max(1, rl.limit) < 0.2;
  return (
    <p className={`headroom${low ? ' headroom-low' : ''}`}>
      {rl.remaining}
      {rl.limit !== null ? ` of ${rl.limit}` : ''} reads left before the platform limit
      {rl.reset_at ? `, resets ${localDateTime(rl.reset_at)}` : ''}.
    </p>
  );
}

function Results({ result, platform }: { result: FeedResult; platform: string }) {
  if (result.outcome === 'adapter_failure') {
    return (
      <EmptyState
        tone="bad"
        heading={interpolate(EMPTY_STATES.feedAdapterFailure.heading, { platform })}
        body={EMPTY_STATES.feedAdapterFailure.body}
      >
        {result.detail ? <p className="muted">{result.detail}</p> : null}
      </EmptyState>
    );
  }

  if (result.outcome === 'rate_limited') {
    return (
      <EmptyState
        tone="warn"
        heading={interpolate(EMPTY_STATES.feedRateLimited.heading, { platform })}
        body={interpolate(EMPTY_STATES.feedRateLimited.bodyTemplate, {
          resetAt: localDateTime(result.rate_limit.reset_at),
          age: relativeAge(result.cache_age_seconds),
        })}
      />
    );
  }

  if (result.outcome === 'empty' || result.posts.length === 0) {
    return <EmptyState heading={EMPTY_STATES.feedEmpty.heading} body={EMPTY_STATES.feedEmpty.body} />;
  }

  return (
    <ul className="feed-list">
      {result.posts.map((p) => (
        <li key={p.external_id} className="feed-item">
          <p className="feed-author">@{p.author_handle}</p>
          <p className="feed-body">{p.body}</p>
          <p className="muted">
            {localDateTime(p.created_at)}
            {p.url ? (
              <>
                {' · '}
                <a href={p.url} target="_blank" rel="noreferrer noopener">
                  Open
                </a>
              </>
            ) : null}
          </p>
        </li>
      ))}
    </ul>
  );
}

export function FeedSearch({ bot, caps }: { bot: Bot; caps: CapabilityResolver }) {
  const feed = useApi<FeedResult>(
    caps.visible('read_posts') ? routes.content.feed(bot.id) : null,
    { query: { limit: 20 } },
  );
  const [q, setQ] = useState('');
  // §10.4 makes search a GET with a `q` parameter, so it is a query keyed on the
  // submitted term rather than a mutation. `submitted` is what the user pressed
  // the button on; `q` is only what is in the box.
  const [submitted, setSubmitted] = useState('');
  const search = useApi<FeedResult>(
    submitted && caps.visible('search') ? routes.content.search(bot.id) : null,
    { query: { q: submitted, limit: 20 } },
  );
  const platform = preferredAccount(bot.social_accounts)?.platform ?? 'the platform';

  return (
    <div className="feed-region">
      <Gate caps={caps} capability="read_posts">
        {(disabled) => (
          <Card
            title="Feed"
            actions={
              <button type="button" className="btn btn-sm" onClick={feed.reload} disabled={disabled}>
                Refresh
              </button>
            }
          >
            {feed.initialLoading ? <Loading label="Reading the feed" /> : null}
            {feed.problem ? <ErrorView problem={feed.problem} onRetry={feed.reload} /> : null}
            {feed.data ? (
              <>
                <CacheLine result={feed.data} />
                <Results result={feed.data} platform={platform} />
              </>
            ) : null}
          </Card>
        )}
      </Gate>

      <Gate caps={caps} capability="search">
        {(disabled) => (
          <Card title="Search">
            {/* Headroom is shown before the search runs, not after it is refused. */}
            <Headroom result={search.data ?? feed.data} />
            <form
              onSubmit={(e) => {
                e.preventDefault();
                setSubmitted(q.trim());
              }}
            >
              <fieldset disabled={disabled}>
                <Field id="feed-q" label={`Search ${platform}`}>
                  <input
                    id="feed-q"
                    className="input"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                  />
                </Field>
                <button type="submit" className="btn btn-primary" disabled={disabled || !q.trim()}>
                  {search.loading ? 'Searching…' : 'Search'}
                </button>
              </fieldset>
            </form>
            {search.problem ? <ErrorView problem={search.problem} /> : null}
            {search.data ? (
              <>
                <CacheLine result={search.data} />
                <Results result={search.data} platform={platform} />
              </>
            ) : null}
          </Card>
        )}
      </Gate>
    </div>
  );
}
